<template>
  <div class="select-input-container">
    <FormLabel
      :label="label"
      :isRequired="isRequired && !readOnly"
      v-if="label"
    />
    <div class="select-input-wrapper">
      <!-- readonlyモードではテキスト表示 -->
      <div v-if="readOnly" class="readonly-text">
        {{ displayValue }}
      </div>
      <!-- 通常モードではtreeselect -->
      <treeselect
        v-else
        :value="value"
        :options="options"
        :disabled="disabled"
        :multiple="multiple"
        :clearable="clearable"
        :placeholder="placeholder"
        :noOptionsText="noOptionsText"
        :limit="limit"
        :limitText="limitText"
        @input="onInput"
      />
    </div>
    <InputBottomDivider v-if="readOnly" />
  </div>
</template>

<script>
import FormLabel from "./FormLabel.vue";
import InputBottomDivider from "./InputBottomDivider.vue";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";

export default {
  name: "SelectInput",
  components: {
    FormLabel,
    InputBottomDivider,
    Treeselect,
  },
  props: {
    /**
     * ラベルテキスト
     * @type {String}
     * @required
     */
    label: {
      type: String,
    },
    /**
     * 選択値
     * @type {String|Number|Array}
     * @default null
     */
    value: {
      type: [String, Number, Array],
      default: null,
    },
    /**
     * 選択肢の配列
     * @type {Array}
     * @default () => []
     * @example [{ id: 1, label: "選択肢1" }, { id: 2, label: "選択肢2" }]
     */
    options: {
      type: Array,
      default: () => [],
    },
    /**
     * プレースホルダー
     * @type {String}
     * @default "選択してください"
     */
    placeholder: {
      type: String,
      default: "選択してください",
    },
    /**
     * 必須項目かどうか
     * @type {Boolean}
     * @default false
     */
    isRequired: {
      type: Boolean,
      default: false,
    },
    /**
     * 読み取り専用かどうか
     * @type {Boolean}
     * @default false
     */
    readOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * 無効化されているかどうか
     * @type {Boolean}
     * @default false
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * 複数選択可能かどうか
     * @type {Boolean}
     * @default false
     */
    multiple: {
      type: Boolean,
      default: false,
    },
    /**
     * クリア可能かどうか
     * @type {Boolean}
     * @default false
     */
    clearable: {
      type: Boolean,
      default: false,
    },
    /**
     * オプションがない場合のテキスト
     * @type {String}
     * @default "なし"
     */
    noOptionsText: {
      type: String,
      default: "なし",
    },
    /**
     * 表示する選択肢の最大数（複数選択時）
     * @type {Number}
     * @default undefined
     */
    limit: {
      type: Number,
      default: undefined,
    },
    /**
     * 制限を超えた場合のテキスト（関数または文字列）
     * @type {Function|String}
     * @default undefined
     * @example count => `+${count}`
     */
    limitText: {
      type: [Function, String],
      default: undefined,
    },
  },
  computed: {
    displayValue() {
      if (
        this.value === null ||
        this.value === undefined ||
        this.value === ""
      ) {
        return "-";
      }

      // 複数選択の場合
      if (Array.isArray(this.value) && this.value.length > 0) {
        const labels = this.value
          .map(val => {
            const option = this.options.find(
              opt => opt.id === val || opt.id === String(val)
            );
            return option ? option.label : val;
          })
          .filter(Boolean);
        return labels.length > 0 ? labels.join(", ") : "-";
      }

      // 単一選択の場合
      const option = this.options.find(
        opt => opt.id === this.value || opt.id === String(this.value)
      );
      return option ? option.label : this.value;
    },
  },
  methods: {
    onInput(value) {
      this.$emit("input", value);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";

.select-input-container {
  display: flex;
  flex-direction: column;

  .select-input-wrapper {
    .readonly-text {
      padding: 0rem 0.75rem;
      font-family: $aisea-font-family;
      font-size: 16px;
      font-weight: 400;
      color: $aisea-dark-gray;
      line-height: 1.5;
      min-height: 26px;
    }

    ::v-deep .vue-treeselect {
      .vue-treeselect__control {
        border: 1px solid $aisea-mouse-gray;
        border-radius: 5px;
        min-height: 36px;
      }

      .vue-treeselect__control--focused {
        border-color: #2d9cdb;
        box-shadow: 0 0 0 2px rgba(45, 156, 219, 0.1);
      }

      &.vue-treeselect--disabled {
        .vue-treeselect__control {
          background-color: #f5f5f5;
          border-color: $aisea-light-gray;
          cursor: not-allowed;
        }
      }
    }
  }
}
</style>
